/**
 * AX auditor (RFC §7a) — the MACHINE-CHECKABLE tier only.
 *
 * Checked: AX-1 (domain-noun tags with registered prefixes), AX-3 (data-act on
 * real button/a, registered verb, payload where declared), AX-4 (urn validity,
 * entity surfaces carry urn, id uniqueness), AX-5 (valid aria-current /
 * aria-live values; role="log" wants aria-live), AX-9 (structural vocabulary —
 * attribute names, classes, verbs — is ASCII English), and the GRADUATED AX-2
 * state-class registry (unregistered `is-*` = warning; `is-x-*` always legal;
 * escalates to error only when the surface is declared `stable` — §7a).
 *
 * Deliberately NOT checked: AX-6 (numbers ship with meaning), AX-7 (no
 * wall-of-text wrappers), AX-10 (decision-relevant state rendered). Those are
 * judgment calls, not lint rules — §7a assigns them to the review-guidelines
 * tier (builder+evaluator loop + golden-snapshot diffs), and pretending they
 * are decidable was the calibration trap the review flagged.
 *
 * Severity mapping (§7a): hard-tier findings are `warning` in `mode:"dev"`
 * (console, never blocking, never throwing) and `error` in `mode:"ci"` (the
 * golden harness turns them into test failures). AX-2 stays `warning` in both
 * modes until `stable: true`. `auditAx` itself NEVER throws — it reports.
 */

import type { Dispose } from "../core/viewmodel";
import type { AxVocabulary } from "./vocabulary";

export type AxRule = "AX-1" | "AX-2" | "AX-3" | "AX-4" | "AX-5" | "AX-9";

export interface AxViolation {
  rule: AxRule;
  severity: "warning" | "error";
  /** Selector-ish path from the audit root, e.g. "career-card > button:nth-of-type(2)". */
  node: string;
  message: string;
}

export interface AxReport {
  violations: AxViolation[];
}

export interface AuditOptions {
  vocabulary: AxVocabulary;
  /** Tag prefixes allowed by AX-1. Defaults to the vocabulary's apps. */
  tagPrefixes?: readonly string[];
  mode: "dev" | "ci";
  /** §7a escalation: unregistered non-is-x state classes become errors. */
  stable?: boolean;
}

const ARIA_CURRENT_VALUES = new Set([
  "page",
  "step",
  "location",
  "date",
  "time",
  "true",
  "false",
]);
const ARIA_LIVE_VALUES = new Set(["off", "polite", "assertive"]);
const ASCII_RE = /^[\x20-\x7E]+$/;

/** Selector-ish path of `el` relative to (and excluding) `root`. */
const nodePath = (el: Element, root: Element): string => {
  const parts: string[] = [];
  let cur: Element | null = el;
  while (cur && cur !== root) {
    const tag = cur.localName;
    const parent: Element | null = cur.parentElement;
    let suffix = "";
    if (parent) {
      const sameTag = [...parent.children].filter((c) => c.localName === tag);
      if (sameTag.length > 1) {
        suffix = `:nth-of-type(${sameTag.indexOf(cur) + 1})`;
      }
    }
    parts.unshift(`${tag}${suffix}`);
    cur = parent;
  }
  return parts.length > 0 ? parts.join(" > ") : root.localName;
};

export function auditAx(root: Element, options: AuditOptions): AxReport {
  const { vocabulary, mode, stable = false } = options;
  const prefixes = options.tagPrefixes ?? vocabulary.apps;
  const violations: AxViolation[] = [];
  const hardSeverity = mode === "ci" ? "error" : "warning";

  const report = (rule: AxRule, severity: AxViolation["severity"], el: Element, message: string) =>
    violations.push({ rule, severity, node: nodePath(el, root), message });

  const seenIds = new Map<string, Element>();
  const elements = [root, ...root.querySelectorAll("*")];

  for (const el of elements) {
    const tag = el.localName;

    // AX-1: custom-element tags are domain nouns with a registered prefix.
    if (tag.includes("-")) {
      const ok = prefixes.some((p) => tag.startsWith(`${p}-`));
      if (!ok) {
        report(
          "AX-1",
          hardSeverity,
          el,
          `<${tag}> has no registered prefix (allowed: ${prefixes.map((p) => `${p}-`).join(", ")}). ` +
            `Tags are domain nouns, app-prefixed (AX-1).`
        );
      }
    }

    // AX-3: every affordance is a data-act verb on a real button/a.
    if (el.hasAttribute("data-act")) {
      const verb = el.getAttribute("data-act") ?? "";
      if (tag !== "button" && tag !== "a") {
        report(
          "AX-3",
          hardSeverity,
          el,
          `data-act="${verb}" sits on <${tag}> — affordances belong on a real <button> or <a> (AX-3).`
        );
      }
      const spec = vocabulary.actVerbs.get(verb);
      if (!spec) {
        report(
          "AX-3",
          hardSeverity,
          el,
          `data-act verb "${verb}" is not in the registered vocabulary (AX-3).`
        );
      } else if (spec.payloadRequired) {
        const hasPayload = el
          .getAttributeNames()
          .some((n) => n.startsWith("data-") && n !== "data-act");
        if (!hasPayload) {
          report(
            "AX-3",
            hardSeverity,
            el,
            `verb "${verb}" is declared payload-bearing but carries no data-* payload (AX-3).`
          );
        }
      }
    }

    // AX-4: identity is visible.
    if (el.hasAttribute("urn")) {
      const urn = el.getAttribute("urn") ?? "";
      if (!urn.startsWith("urn:")) {
        report(
          "AX-4",
          hardSeverity,
          el,
          `urn attribute "${urn}" is not a urn: identifier (AX-4).`
        );
      }
    } else if (vocabulary.entitySurfaces.has(tag)) {
      report(
        "AX-4",
        hardSeverity,
        el,
        `<${tag}> is a registered entity surface but carries no urn attribute (AX-4).`
      );
    }
    const id = el.getAttribute("id");
    if (id) {
      const prior = seenIds.get(id);
      if (prior) {
        report(
          "AX-4",
          hardSeverity,
          el,
          `duplicate id "${id}" (also on ${nodePath(prior, root)}) — ids must be stable addresses (AX-4).`
        );
      } else {
        seenIds.set(id, el);
      }
    }

    // AX-5: ARIA is truth.
    const ariaCurrent = el.getAttribute("aria-current");
    if (ariaCurrent !== null && !ARIA_CURRENT_VALUES.has(ariaCurrent)) {
      report(
        "AX-5",
        hardSeverity,
        el,
        `aria-current="${ariaCurrent}" is not a valid value (${[...ARIA_CURRENT_VALUES].join("|")}) (AX-5).`
      );
    }
    const ariaLive = el.getAttribute("aria-live");
    if (ariaLive !== null && !ARIA_LIVE_VALUES.has(ariaLive)) {
      report(
        "AX-5",
        hardSeverity,
        el,
        `aria-live="${ariaLive}" is not a valid value (off|polite|assertive) (AX-5).`
      );
    }
    if (el.getAttribute("role") === "log" && ariaLive === null) {
      report(
        "AX-5",
        "warning",
        el,
        `role="log" without aria-live — streaming regions should declare their politeness (AX-5).`
      );
    }

    // AX-9 + AX-2 over class tokens; AX-9 over attribute names.
    for (const name of el.getAttributeNames()) {
      if (!ASCII_RE.test(name)) {
        report(
          "AX-9",
          hardSeverity,
          el,
          `attribute name "${name}" is not ASCII — structural vocabulary is English (AX-9).`
        );
      }
    }
    const actVerb = el.getAttribute("data-act");
    if (actVerb !== null && !ASCII_RE.test(actVerb)) {
      report(
        "AX-9",
        hardSeverity,
        el,
        `data-act verb "${actVerb}" is not ASCII — verbs are English (AX-9).`
      );
    }
    for (const cls of el.classList) {
      if (!ASCII_RE.test(cls)) {
        report(
          "AX-9",
          hardSeverity,
          el,
          `class "${cls}" is not ASCII — structural vocabulary is English (AX-9).`
        );
        continue;
      }
      // AX-2 (graduated): registered is-*; is-x-* always legal.
      if (cls.startsWith("is-") && !cls.startsWith("is-x-")) {
        if (!vocabulary.stateClasses.has(cls)) {
          report(
            "AX-2",
            stable ? "error" : "warning",
            el,
            `state class "${cls}" is not registered (AX-2). Register it or use the ` +
              `is-x-* exploration namespace${stable ? " — this surface is declared stable" : ""}.`
          );
        }
      }
    }
  }

  return { violations };
}

export interface DevAuditorOptions {
  tagPrefixes?: readonly string[];
  /** Debounce for mutation bursts. Default 100ms. */
  debounceMs?: number;
}

/**
 * Dev-mode auditor: MutationObserver-driven debounced re-audit that logs NEW
 * violations via console.warn (never blocking, never throwing — §7a). No-op
 * (noop dispose) when MutationObserver is unavailable.
 */
export function installDevAuditor(
  root: Element,
  vocabulary: AxVocabulary,
  opts: DevAuditorOptions = {}
): Dispose {
  const MO = (globalThis as { MutationObserver?: typeof MutationObserver })
    .MutationObserver;
  if (!MO) return () => {};

  const debounceMs = opts.debounceMs ?? 100;
  const seen = new Set<string>();
  let timer: ReturnType<typeof setTimeout> | null = null;

  const run = () => {
    const { violations } = auditAx(root, {
      vocabulary,
      mode: "dev",
      ...(opts.tagPrefixes ? { tagPrefixes: opts.tagPrefixes } : {}),
    });
    for (const v of violations) {
      const key = `${v.rule}|${v.node}|${v.message}`;
      if (seen.has(key)) continue;
      seen.add(key);
      console.warn(`[web-kit AX] ${v.rule} at ${v.node}: ${v.message}`);
    }
  };

  const observer = new MO(() => {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(run, debounceMs);
  });
  observer.observe(root, {
    subtree: true,
    childList: true,
    attributes: true,
  });
  run(); // initial pass

  return () => {
    observer.disconnect();
    if (timer !== null) clearTimeout(timer);
  };
}
